import React from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';

interface IndustryCardProps {
  industry: {
    id: string;
    name: string;
    icon: string;
    image: string;
    alt: string;
    description: string;
    challenges: string[];
    solutions: string[];
    compliance: string[];
    caseStudyCount: number;
  };
}

const IndustryCard: React.FC<IndustryCardProps> = ({ industry }) => {
  return (
    <div className="bg-card rounded-xl shadow-card hover:shadow-elevated transition-all duration-300 overflow-hidden group">
      <div className="relative h-48 overflow-hidden">
        <AppImage
          src={industry.image}
          alt={industry.alt}
          className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-500"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
        <div className="absolute bottom-4 left-4 flex items-center space-x-3">
          <div className="w-12 h-12 bg-white rounded-lg flex items-center justify-center shadow-card">
            <Icon name={industry.icon as any} size={24} className="text-brand-primary" />
          </div>
          <h3 className="text-2xl font-headline font-bold text-white">{industry.name}</h3>
        </div>
      </div>

      <div className="p-6 space-y-4">
        <p className="text-muted-foreground font-body leading-relaxed">{industry.description}</p>

        <div className="space-y-3">
          <div>
            <h4 className="text-sm font-cta font-semibold text-primary mb-2 flex items-center space-x-2">
              <Icon name="ExclamationTriangleIcon" size={16} variant="solid" className="text-warning" />
              <span>Key Challenges</span>
            </h4>
            <ul className="space-y-1">
              {industry.challenges.slice(0, 3).map((challenge, idx) => (
                <li key={idx} className="text-sm text-muted-foreground flex items-start space-x-2">
                  <Icon name="ChevronRightIcon" size={16} className="text-brand-secondary mt-0.5 flex-shrink-0" />
                  <span>{challenge}</span>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="text-sm font-cta font-semibold text-primary mb-2 flex items-center space-x-2">
              <Icon name="CheckCircleIcon" size={16} variant="solid" className="text-success" />
              <span>Our Solutions</span>
            </h4>
            <ul className="space-y-1">
              {industry.solutions.slice(0, 3).map((solution, idx) => (
                <li key={idx} className="text-sm text-muted-foreground flex items-start space-x-2">
                  <Icon name="ChevronRightIcon" size={16} className="text-brand-secondary mt-0.5 flex-shrink-0" />
                  <span>{solution}</span>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="text-sm font-cta font-semibold text-primary mb-2 flex items-center space-x-2">
              <Icon name="ShieldCheckIcon" size={16} variant="solid" className="text-trust-builder" />
              <span>Compliance Expertise</span>
            </h4>
            <div className="flex flex-wrap gap-2">
              {industry.compliance.map((cert, idx) => (
                <span
                  key={idx}
                  className="px-3 py-1 bg-muted text-xs font-cta font-semibold text-primary rounded-full"
                >
                  {cert}
                </span>
              ))}
            </div>
          </div>
        </div>

        <div className="pt-4 border-t border-border flex items-center justify-between">
          <div className="flex items-center space-x-2 text-sm text-muted-foreground">
            <Icon name="DocumentTextIcon" size={16} />
            <span>{industry.caseStudyCount} Case Studies</span>
          </div>
          <Link
            href={`/industry-solutions/${industry.id}`}
            className="inline-flex items-center space-x-2 px-4 py-2 bg-brand-primary hover:bg-brand-primary/90 text-white rounded-lg font-cta font-semibold text-sm transition-all duration-300 transform hover:scale-105"
          >
            <span>Learn More</span>
            <Icon name="ArrowRightIcon" size={16} />
          </Link>
        </div>
      </div>
    </div>
  );
};

export default IndustryCard;